import React, { type ReactNode, Suspense, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { getTrialDaysLeft } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { bytesToGigabytes } from '../services/bytes-to-gigabytes';
import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { ExperimentContext, ExperimentContextClass } from '../services/experiment-context';
import { getAutomationFreeLimitStatus } from '../services/get-automation-free-limit-status';
import { getMostCriticalStorage } from '../services/get-most-critical-storage';
import { getProductCollection } from '../services/get-product-collection';
import { getQueryConfig } from '../services/get-query-config';
import { getSiteTraitValues } from '../services/get-trait-values';
import { isProductOneOf } from '../services/is-product-one-of';
import { STANDARD_TO_PREMIUM_TRAIT_ELIGIBILITY } from '../services/standard-to-premium-traits';
import { getTwcOfferingIdForEdition } from '../services/twc-offerings';
import type {
	Action,
	ActionId,
	Edition,
	Environment,
	Flag,
	LastKnownTrial,
	PremiumWrapperProps,
	Product,
	Trait,
	UserRole,
} from '../types';
import type { editionAwarenessQuery } from './__generated__/editionAwarenessQuery.graphql';
import { ActionsAndContent } from './actions-and-content';
import { ErrorBoundary } from './error-boundary';
export type EditionAwarenessProps = {
	/**
	 * This is used to inform the component that it can render, dependencies that need to be fetched
	 * outside of product should use this for the loading state.
	 *
	 * The reason for passing through `canRender` and not just returning `null` when `canRender` is false
	 * is to ensure that the edition awareness query can run as early as possible. This means that if the parameters being
	 * passed through are used in the query we can not use `canRender` for those as the query will run before we have
	 * the values.
	 */
	canRender: boolean;
	cloudId: string;
	/**
	 * Indicates which active route the user is currently on.
	 */
	currentRoute?: 'ops';
	edition: Edition;
	/**
	 * The CCP entitlement id for the current product, if the product has it available.
	 * When provided, the value resolved from the edition awareness query is still preferred;
	 * this is only used as a fallback when the query does not return one.
	 */
	entitlementId?: string | null;
	environment?: Environment;
	/**
	 * Get the in product action associated with the action id if available
	 */
	getAction: (id: ActionId) => Promise<Action<ActionId> | null>;
	/**
	 * For the Standard to Premium button that can be dismissed
	 */
	hasDismissedButton?: boolean;
	/**
	 * Dismisses a flag from in-product
	 */
	hideFlag?: (id: string) => void;
	/** @deprecated avoid auto opening the dialogs */
	onAutoOpen?: () => void;
	onDismissButton?: () => void;
	orgId?: string;
	/** @deprecated likely to be removed in the future */
	premiumFeatureHighlightToggle?: ReactNode;
	/**
	 * Confluence in premium has a lot of specific packages that are wired up with the button,
	 * we're therefore passing it through to edition awareness to prevent fragmenting the implementation
	 * of those features (e.g. needing to replicate/shift the logic for button border nudges into platform)
	 *
	 * @deprecated this will be removed, anything new should be added directly inside of the package and not in the product
	 */
	PremiumWrapper?: (props: PremiumWrapperProps) => ReactNode;
	product: Product;
	/** @deprecated avoid auto opening the dialogs */
	shouldAutoOpen?: boolean;
	/**
	 * Calls the in-product flag provider to show the flag to the customer
	 */
	showFlag?: (flag: Flag) => void;
	siteTraits?: Trait[];
	userDisplayName?: string | null;
	userEmail?: string | null;
	/** The Atlassian account id of the current user */
	userId?: string | null;
	userRoles?: UserRole[];
};

const PREDUNNING_LENGTH = 14;
const MILLISECONDS_PER_DAY = 86_400_000;
const FIX_PDE_DETECTION_WITH_INVOICEABLE_GATE = 'flymodst-121-fix-pde-detection-with-invoiceable';

/**
 * Edition awareness is used to communicate to customers about the current edition they have and if they should
 * - Add payment details
 * - Upgrade their edition
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessImpl(props: EditionAwarenessProps) {
	const {
		cloudId,
		product,
		edition,
		orgId,
		userId,
		userDisplayName,
		userEmail,
		userRoles: userRolesFromProduct,
		environment,
		entitlementId: entitlementIdFromProduct,
		siteTraits = [],
		canRender,
		currentRoute,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
		getAction,
		showFlag,
		hideFlag,
		hasDismissedButton,
		onDismissButton,
	} = props;

	const queryConfig = getQueryConfig(
		product,
		edition,
		cloudId,
		userRolesFromProduct,
		entitlementIdFromProduct,
	);

	const data = useLazyLoadQuery<editionAwarenessQuery>(
		graphql`
			query editionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldCheckForDirectBuyChangeOffering: Boolean!
				$shouldCheckForTrial: Boolean!
				$shouldCheckForSubscription: Boolean!
				$shouldCheckForChargeQuantities: Boolean!
				$entitlementFilter: CommerceEntitlementFilter
				$chargeElement: String!
				$shouldCheckForStorageUsageJira: Boolean!
				$applicationKey: JiraApplicationKey!
				$shouldCheckForStorageLimit: Boolean!
				$shouldCheckForPaymentDetails: Boolean!
				$shouldCheckForAddPaymentDetails: Boolean!
				$isServiceCollectionFree: Boolean = false
				$entitlementId: ID!
				$shouldCheckForSinglePaidApp: Boolean = false
				$shouldCheckCanBundleCollection: Boolean = false
				$shouldCheckCanBundlePermissionThruEntitlement: Boolean = false
			) {
				jira @include(if: $shouldCheckForStorageUsageJira) {
					attachmentStorageAllowed(cloudId: $cloudId, applicationKey: $applicationKey)
						@include(if: $shouldCheckForStorageLimit)
					attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: $applicationKey)
						@include(if: $shouldCheckForStorageLimit)
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
				}

				jsm_storage: jira @include(if: $isServiceCollectionFree) {
					attachmentStorageAllowed(cloudId: $cloudId, applicationKey: JIRA_SERVICE_DESK)
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: JIRA_SERVICE_DESK)
					attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: JIRA_SERVICE_DESK)
				}

				csm_storage: jira @include(if: $isServiceCollectionFree) {
					attachmentStorageAllowed(cloudId: $cloudId, applicationKey: JIRA_CUSTOMER_SERVICE)
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: JIRA_CUSTOMER_SERVICE)
					attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: JIRA_CUSTOMER_SERVICE)
				}

				growthUnifiedProfile_getEntitlementProfile(entitlementId: $entitlementId)
					@include(if: $shouldCheckForSinglePaidApp) {
					availableCollectionSegments {
						collectionOfferingId
						cannibalisationRiskSegment
						existingStandaloneApps {
							entitlementId
							isAutoProvisionedUnactivated
						}
					}
				}

				tenantContexts(cloudIds: [$cloudId]) {
					transactionAccounts @include(if: $shouldCheckCanBundleCollection) {
						isCurrentUserBillingAdmin
					}

					entitlementInfo(hamsProductKey: $productKey) {
						entitlementId
						... on CommerceEntitlementInfoCcp {
							entitlement(where: $entitlementFilter) {
								experienceCapabilities {
									changeOfferingV2(offeringName: "Standard") {
										isAvailableToUser
										experienceUrl
									}

									changeOfferingV2WithSkipTrial: changeOfferingV2(
										offeringName: "Standard"
										skipTrial: true
									) @include(if: $shouldCheckForDirectBuyChangeOffering) {
										isAvailableToUser
										experienceUrl
									}
								}

								latestUsageForChargeElement(chargeElement: $chargeElement)
									@include(if: $shouldCheckForChargeQuantities)

								preDunning @include(if: $shouldCheckForTrial) {
									status
								}

								subscription @include(if: $shouldCheckForSubscription) {
									trial @include(if: $shouldCheckForTrial) {
										timeLeft
										startTimestamp
										endTimestamp
									}
									accountDetails {
										invoiceGroup {
											invoiceable
											experienceCapabilities @include(if: $shouldCheckForTrial) {
												configurePaymentV2 {
													experienceUrl
													isAvailableToUser
												}
											}
										}
									}
								}
								offering @include(if: $shouldCheckForTrial) {
									trial {
										lengthDays
									}
									key
								}

								transactionAccount {
									key
									isCurrentUserBillingAdmin
								}

								transactionAccount @include(if: $shouldCheckForPaymentDetails) {
									isBillToPresent
								}

								transactionAccount @include(if: $shouldCheckForAddPaymentDetails) {
									experienceCapabilities {
										addPaymentMethodV2 {
											isAvailableToUser
											experienceUrl
										}
									}
								}

								relatesFromEntitlementsWithEnterpriseType: relatesFromEntitlements(
									relationshipTypes: "ENTERPRISE"
								) {
									relationshipType
									entitlementId
								}

								relatesFromEntitlementsWithCollectionType: relatesFromEntitlements(
									relationshipTypes: "COLLECTION"
								) {
									relationshipType
									entitlementId
									entitlement {
										preDunning {
											status
										}

										entitlementProfile {
											lastKnownTrial {
												trialType
												trialTrigger
												trialEndTimeStamp
												createdAt
												offeringId
											}
										}
									}
								}

								entitlementProfile {
									lastKnownTrial {
										trialType
										trialTrigger
										trialEndTimeStamp
										createdAt
										offeringId
									}
								}
							}
						}
					}

					bundlePermissionEntitlementInfo: entitlementInfo(hamsProductKey: $productKey)
						@include(if: $shouldCheckCanBundlePermissionThruEntitlement) {
						... on CommerceEntitlementInfoCcp {
							entitlement {
								transactionAccount {
									isCurrentUserBillingAdmin
								}
							}
						}
					}
				}
			}
		`,
		queryConfig,
	);

	const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;
	const entitlement = entitlementInfo?.entitlement;
	const entitlementId = entitlementInfo?.entitlementId ?? entitlementIdFromProduct;

	const transactionAccounts = data?.tenantContexts?.[0]?.transactionAccounts;
	const canBundleCollection =
		!!transactionAccounts &&
		transactionAccounts.length > 0 &&
		transactionAccounts.every((txa) => txa?.isCurrentUserBillingAdmin === true);

	const bundlePermissionEntitlementInfo =
		data?.tenantContexts?.[0]?.bundlePermissionEntitlementInfo;
	const permitToBundleCollection =
		bundlePermissionEntitlementInfo?.entitlement?.transactionAccount?.isCurrentUserBillingAdmin ===
		true;

	const siteTraitsForContext = useMemo(() => getSiteTraitValues(siteTraits), [siteTraits]);

	const standardToPremiumTraitKey = STANDARD_TO_PREMIUM_TRAIT_ELIGIBILITY[product];
	// This entitlement was filter because was not on trial or predunning, but use the traits to validate is indeed a paid site
	const isEligibleForStandardToPremium = Boolean(
		edition === 'standard' &&
		!entitlement &&
		standardToPremiumTraitKey &&
		siteTraitsForContext[standardToPremiumTraitKey],
	);

	const invoiceGroupInvoiceable =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable;
	const invoiceableStatus =
		isEligibleForStandardToPremium || invoiceGroupInvoiceable === true
			? 'INVOICEABLE'
			: invoiceGroupInvoiceable === false
				? 'NOT_INVOICEABLE'
				: 'UNKNOWN';

	const invoiceGroupPaymentConfig =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2;
	const canAddInvoiceGroupPayment = invoiceGroupPaymentConfig
		? Boolean(
				invoiceGroupPaymentConfig.isAvailableToUser && invoiceGroupPaymentConfig.experienceUrl,
			)
		: null;
	const transactionAccountPaymentConfig =
		entitlement?.transactionAccount?.experienceCapabilities?.addPaymentMethodV2;
	const canAddTxaPayment = transactionAccountPaymentConfig
		? Boolean(
				transactionAccountPaymentConfig.isAvailableToUser &&
				transactionAccountPaymentConfig.experienceUrl,
			)
		: null;

	// Use invoice group configurePaymentV2 to determine can add payment and experience url by default
	// If invoice group does not exist (for example free edition skip trial scenario), fall back to txa addPaymentMethodV2 if possible
	const canAddPaymentDetails = canAddInvoiceGroupPayment ?? canAddTxaPayment;

	const addPaymentDetailsExperienceUrl = invoiceGroupPaymentConfig
		? invoiceGroupPaymentConfig.experienceUrl
		: edition === 'free'
			? transactionAccountPaymentConfig?.experienceUrl
			: null;

	const hasPaymentDetailsEntered = isEligibleForStandardToPremium
		? true
		: fg(FIX_PDE_DETECTION_WITH_INVOICEABLE_GATE)
			? Boolean(invoiceGroupInvoiceable ?? entitlement?.transactionAccount?.isBillToPresent)
			: Boolean(entitlement?.transactionAccount?.isBillToPresent);

	const trialTimeLeft = entitlement?.subscription?.trial?.timeLeft ?? undefined;
	const trialDaysLeft = getTrialDaysLeft(trialTimeLeft);

	const trialStartTimestamp = entitlement?.subscription?.trial?.startTimestamp;
	const trialStartDate = useMemo(
		() => (trialStartTimestamp ? new Date(trialStartTimestamp) : undefined),
		[trialStartTimestamp],
	);

	const trialEndTimestamp = entitlement?.subscription?.trial?.endTimestamp;
	const trialEndDate = useMemo(
		() => (trialEndTimestamp ? new Date(trialEndTimestamp) : undefined),
		[trialEndTimestamp],
	);

	const trialLengthDays = entitlement?.offering?.trial?.lengthDays ?? undefined;

	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);

	const isEntitlementPredunningStatus = entitlement?.preDunning?.status;
	const isCollectionEntitlementPredunningStatus =
		entitlement?.relatesFromEntitlementsWithCollectionType?.[0]?.entitlement?.preDunning?.status;
	const isInPredunning =
		(isEntitlementPredunningStatus === 'IN_PRE_DUNNING' ||
			isCollectionEntitlementPredunningStatus === 'IN_PRE_DUNNING') &&
		invoiceableStatus === 'NOT_INVOICEABLE';

	const { isInCollection, collectionEntitlementId } = useMemo<
		Pick<EditionAwarenessContextType, 'isInCollection' | 'collectionEntitlementId'>
	>(() => {
		// The COLLECTION-filtered alias is the single source of truth for collection membership.
		// The query filters it on `relationshipTypes: "COLLECTION"`, so every entry it returns is
		// already a collection relationship and there is no need to match on `relationshipType`.
		const collectionRelationships = entitlement?.relatesFromEntitlementsWithCollectionType;
		const collectionEntitlement = collectionRelationships?.[0];

		return {
			isInCollection:
				!entitlement || !collectionRelationships
					? 'UNKNOWN'
					: // `entitlementId` is nullable in the schema, and consumers of
						// `collectionEntitlementId` build an entitlement ARI out of it (see
						// `collection-change-plan-popup`). Requiring it here keeps that id usable
						// whenever we report IN_COLLECTION.
						collectionEntitlement?.entitlementId
						? 'IN_COLLECTION'
						: 'NOT_IN_COLLECTION',
			collectionEntitlementId: collectionEntitlement?.entitlementId,
		};
	}, [entitlement]);

	const isChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
	const changeOfferingToStandardExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;
	const isDirectBuyChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2WithSkipTrial?.isAvailableToUser;
	const directBuyChangeOfferingToStandardExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2WithSkipTrial?.experienceUrl;

	const canUpgrade =
		// Site admin check is not added to the eligibility check, as all the other fields are site-level properties, while this one is at user-site level
		(isEligibleForStandardToPremium && userRolesFromProduct?.includes('SITE_ADMIN')) ||
		Boolean(isChangeOfferingAvailable && changeOfferingToStandardExperienceUrl);
	const canDirectBuyUpgrade = Boolean(
		isDirectBuyChangeOfferingAvailable && directBuyChangeOfferingToStandardExperienceUrl,
	);

	const transactionAccountId = entitlement?.transactionAccount?.key;

	const isEnterpriseCustomer = useMemo(() => {
		// The alias is already server-filtered to ENTERPRISE, so the `relationshipType`
		// check is redundant for real data. It is kept because Relay's MockPayloadGenerator
		// auto-fills unmocked alias fields with a bogus 1-element array (random
		// `relationshipType`, truthy `entitlementId`). Matching on `relationshipType` makes
		// those auto-filled entries fall through to non-enterprise instead of producing a
		// false-positive in tests that do not explicitly mock this alias.
		const enterpriseEntitlement = entitlement?.relatesFromEntitlementsWithEnterpriseType?.find(
			(item) => item?.relationshipType === 'ENTERPRISE',
		);

		return !!enterpriseEntitlement?.entitlementId;
	}, [entitlement?.relatesFromEntitlementsWithEnterpriseType]);

	const productCollection = getProductCollection(product);
	const collectionType =
		(isInCollection === 'IN_COLLECTION' &&
			productCollection?.isEnabled &&
			productCollection?.collectionType) ||
		null;

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [...(userRolesFromProduct || [])];
		if (
			data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
				?.isCurrentUserBillingAdmin
		) {
			roles.push('BILLING_ADMIN');
		}
		return roles;
	}, [data?.tenantContexts, userRolesFromProduct]);

	// We need to use the `latestUsageForChargeElement` to get the licensed user count for both free and trialing customers
	const licensedUsersRaw = entitlement?.latestUsageForChargeElement;
	// Assumes that there are always at least one assigned user to cover scenarios
	const licensedUsers = Math.max(licensedUsersRaw || 0, 1);

	const isStorageLimitsExperimentEnabled = FeatureGates.getExperimentValue<boolean>(
		'jsmgrowth-267_enforce_storage_limits_servco_free',
		'isEnabled',
		false,
	);

	const isServiceCollectionFree =
		edition === 'free' &&
		(product === 'jira-service-management' || product === 'jira-customer-service') &&
		isStorageLimitsExperimentEnabled;

	// TODO: After fg cleanup for jsmgrowth-267_enforce_storage_limits_servco_free, we can
	// destructure criticalStorage directly and remove the manual
	// bytesToGigabytes fallbacks in storageUsageInGB / storageLimitInGB below.
	const criticalStorage = useMemo(() => {
		if (!isStorageLimitsExperimentEnabled) {
			return undefined;
		}

		if (isServiceCollectionFree) {
			return getMostCriticalStorage([
				data.jsm_storage && {
					...data.jsm_storage,
					product: 'jira-service-management',
				},
				data.csm_storage && {
					...data.csm_storage,
					product: 'jira-customer-service',
				},
			]);
		}

		if (isProductOneOf(product, ['jira', 'jira-product-discovery']) && data.jira) {
			return getMostCriticalStorage([
				{
					...data.jira,
					product,
				},
			]);
		}
		return undefined;
	}, [
		data.csm_storage,
		data.jira,
		data.jsm_storage,
		isServiceCollectionFree,
		isStorageLimitsExperimentEnabled,
		product,
	]);

	const storageUsageInGB = useMemo(() => {
		if (criticalStorage && isStorageLimitsExperimentEnabled) {
			return criticalStorage.storageUsageInGB;
		}

		const JIRA_STORAGE_PRODUCTS: Product[] = isStorageLimitsExperimentEnabled
			? ['jira', 'jira-product-discovery']
			: ['jira', 'jira-service-management', 'jira-product-discovery'];
		if (data.jira?.attachmentStorageUsed && isProductOneOf(product, JIRA_STORAGE_PRODUCTS)) {
			return bytesToGigabytes(data.jira.attachmentStorageUsed);
		}

		return undefined;
	}, [
		criticalStorage,
		data.jira?.attachmentStorageUsed,
		isStorageLimitsExperimentEnabled,
		product,
	]);

	const storageLimitInGB = useMemo(() => {
		if (criticalStorage && isStorageLimitsExperimentEnabled) {
			return criticalStorage.storageLimitInGB;
		}

		if (typeof data.jira?.attachmentStorageAllowed === 'number') {
			return bytesToGigabytes(data.jira.attachmentStorageAllowed);
		}
		return undefined;
	}, [criticalStorage, data.jira?.attachmentStorageAllowed, isStorageLimitsExperimentEnabled]);

	const storageIsUnlimited = useMemo(() => {
		if (criticalStorage && isStorageLimitsExperimentEnabled) {
			return criticalStorage.storageIsUnlimited;
		}

		if (typeof data.jira?.attachmentStorageIsUnlimited === 'boolean') {
			return data.jira.attachmentStorageIsUnlimited;
		}
		return undefined;
	}, [criticalStorage, data.jira?.attachmentStorageIsUnlimited, isStorageLimitsExperimentEnabled]);

	const offeringKey = entitlement?.offering?.key;

	const automationFreeLimitStatus = useMemo(
		() =>
			getAutomationFreeLimitStatus({
				product,
				edition,
				...siteTraitsForContext,
			}),
		[product, edition, siteTraitsForContext],
	);

	const lastKnownTrial: LastKnownTrial | undefined = useMemo(() => {
		const entitlementProfile =
			isInCollection === 'IN_COLLECTION'
				? entitlement?.relatesFromEntitlementsWithCollectionType?.[0]?.entitlement
						?.entitlementProfile
				: entitlement?.entitlementProfile;
		// We purposely don't discard the results of the trial if the customer is not on a trial, the reason for this
		// is to enable experiences where the customer has just started a trial, but that change is not yet reflected
		// on the page that has loaded for them.
		const trialEndTimeStamp = entitlementProfile?.lastKnownTrial?.trialEndTimeStamp;

		if (!trialEndTimeStamp) return undefined;

		if (
			(isInTrial && trialEndTimeStamp <= Date.now()) ||
			(isInPredunning && trialEndTimeStamp + PREDUNNING_LENGTH * MILLISECONDS_PER_DAY <= Date.now())
		) {
			return undefined;
		}

		return {
			trialEndTimeStamp: entitlementProfile?.lastKnownTrial?.trialEndTimeStamp,
			trialType: entitlementProfile?.lastKnownTrial?.trialType,
			trialTrigger: entitlementProfile?.lastKnownTrial?.trialTrigger,
			createdAt: entitlementProfile?.lastKnownTrial?.createdAt,
			offeringId: entitlementProfile?.lastKnownTrial?.offeringId,
		};
	}, [
		entitlement?.entitlementProfile,
		isInPredunning,
		isInTrial,
		isInCollection,
		entitlement?.relatesFromEntitlementsWithCollectionType,
	]);

	const isInReverseTrial =
		(isInTrial || isInPredunning) && lastKnownTrial?.trialType === 'REVERSE_TRIAL';

	const availableCollectionSegments =
		data?.growthUnifiedProfile_getEntitlementProfile?.availableCollectionSegments;

	const isSinglePaidApp = useMemo(() => {
		const targetOfferingId = getTwcOfferingIdForEdition(edition);
		const collectionSegment = availableCollectionSegments?.find(
			(segment) => segment.collectionOfferingId === targetOfferingId,
		);

		const isSingleAppPaidSegment =
			collectionSegment?.cannibalisationRiskSegment === 'single_app_paid';

		const otherApps =
			collectionSegment?.existingStandaloneApps?.filter(
				(app) => app.entitlementId !== entitlementId,
			) ?? [];

		const otherAppsAutoProvisionedUnactive = otherApps.every(
			(app) => app.isAutoProvisionedUnactivated === true,
		);

		return isSingleAppPaidSegment && otherAppsAutoProvisionedUnactive;
	}, [edition, entitlementId, availableCollectionSegments]);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			orgId,
			product,
			edition,
			entitlementId,
			invoiceableStatus,
			canAddPaymentDetails,
			hasPaymentDetailsEntered,
			addPaymentDetailsExperienceUrl,
			offeringKey,
			trialDaysLeft,
			trialLengthDays,
			trialStartDate,
			trialEndDate,
			isInTrial,
			isInPredunning,
			isInReverseTrial,
			canUpgrade,
			canDirectBuyUpgrade,
			changeOfferingToStandardExperienceUrl,
			directBuyChangeOfferingToStandardExperienceUrl,
			transactionAccountId,
			isInCollection,
			isEnterpriseCustomer,
			collectionEntitlementId,
			collectionType,
			isSinglePaidApp,
			canBundleCollection,
			permitToBundleCollection,
			userId,
			userDisplayName,
			userEmail,
			userRoles,
			environment,
			licensedUsers,
			storageUsageInGB,
			storageLimitInGB,
			storageIsUnlimited,
			...siteTraitsForContext,
			automationFreeLimitStatus,
			premiumFeatureHighlightToggle,
			shouldAutoOpen,
			onAutoOpen,
			PremiumWrapper,
			lastKnownTrial,
			getAction,
			showFlag,
			hideFlag,
			currentRoute,
			hasDismissedButton,
			onDismissButton,
		};
	}, [
		cloudId,
		orgId,
		product,
		edition,
		entitlementId,
		invoiceableStatus,
		canAddPaymentDetails,
		hasPaymentDetailsEntered,
		addPaymentDetailsExperienceUrl,
		offeringKey,
		trialDaysLeft,
		trialLengthDays,
		trialStartDate,
		trialEndDate,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		canUpgrade,
		canDirectBuyUpgrade,
		changeOfferingToStandardExperienceUrl,
		directBuyChangeOfferingToStandardExperienceUrl,
		transactionAccountId,
		isInCollection,
		isEnterpriseCustomer,
		collectionEntitlementId,
		collectionType,
		isSinglePaidApp,
		canBundleCollection,
		permitToBundleCollection,
		userId,
		userDisplayName,
		userEmail,
		userRoles,
		environment,
		licensedUsers,
		storageUsageInGB,
		storageLimitInGB,
		storageIsUnlimited,
		siteTraitsForContext,
		automationFreeLimitStatus,
		premiumFeatureHighlightToggle,
		shouldAutoOpen,
		onAutoOpen,
		PremiumWrapper,
		lastKnownTrial,
		getAction,
		showFlag,
		hideFlag,
		currentRoute,
		hasDismissedButton,
		onDismissButton,
	]);

	const experimentContextValue = useMemo(() => new ExperimentContextClass(), []);

	if (!canRender) {
		return <UFOLoadHold name="edition-awareness-waiting-on-product" />;
	}

	if (isEnterpriseCustomer) {
		return null;
	}

	return (
		<ExperimentContext.Provider value={experimentContextValue}>
			<EditionAwarenessContext.Provider value={context}>
				<ErrorBoundary product={product} edition={edition} id="audience-and-timing">
					<ActionsAndContent />
				</ErrorBoundary>
			</EditionAwarenessContext.Provider>
		</ExperimentContext.Provider>
	);
}

export function EditionAwareness(props: EditionAwarenessProps): React.JSX.Element {
	const { product, edition } = props;

	return (
		<UFOSegment name="edition-awareness-platform">
			{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
			<ErrorBoundary product={product} edition={edition} id="edition-awareness">
				<Suspense fallback={null}>
					<EditionAwarenessImpl {...props} />
				</Suspense>
			</ErrorBoundary>
		</UFOSegment>
	);
}
